// utils/cvssParser.ts
import type { CVSSEntry } from '@/sections/threats/types';

/**
 * CVSS metric group data structure
 */
export interface CVSSMetricGroupData {
  vector?: string;
  score?: number;
}

/**
 * Parsed CVSS data by version and type
 */
export interface ParsedCVSSData {
  [version: string]: {
    Primary?: Record<string, CVSSMetricGroupData>;
    Secondary?: Record<string, CVSSMetricGroupData>;
  };
}

/**
 * Type guard for valid CVSS metric groups
 */
function isValidMetricGroup(
  group: string
): group is 'Base' | 'Temporal' | 'Threat' {
  return ['Base', 'Temporal', 'Threat'].includes(group);
}

/**
 * Parse CVSS entries into version/type/metricGroup structure
 */
export function parseCVSSData(cvss: CVSSEntry[] | undefined): ParsedCVSSData {
  if (!cvss) return {};

  return cvss.reduce((acc, entry) => {
    const version = entry.version.replace(/[^0-9.]/g, '');
    if (!isValidMetricGroup(entry.metric_group)) return acc;

    const existing = acc[version]?.[entry.type] ?? {};

    return {
      ...acc,
      [version]: {
        ...(acc[version] ?? {}),
        [entry.type]: {
          ...existing,
          [entry.metric_group]: {
            vector: entry.base_vector || entry.temporal_vector,
            score:
              entry.base_score || entry.temporal_score || entry.threat_score,
          },
        },
      },
    };
  }, {} as ParsedCVSSData);
}
